// src/screens/BuyAndSell.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FiInfo, FiX, FiActivity } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { useSettings } from "../settings";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { useWalletId } from "../hooks/useWalletId";

import PullToRefreshHeader from "../components/PullToRefreshHeader";
import { usePullToRefresh } from "../hooks/usePullToRefresh";

/* =========================
   ✅ Vocabulário oficial
========================= */

const VOCAB_STATE = {
  TOP: "Topo",
  PRE_TOP: "Possível Topo",
  VALLEY: "Vale",
  PRE_VALLEY: "Possível Vale",
  NEUTRAL: "Neutro",
};

const VOCAB_REASON = {
  EXHAUSTION_TOP: "Topo (exaustão)",
  RECENT_TOP: "Topo recente",
  EXHAUSTION_PRETOP: "Possível topo (alerta)",
  RECENT_PRETOP: "Possível topo recente",
  OPPORTUNITY_VALLEY: "Vale (oportunidade)",
  RECENT_VALLEY: "Vale recente",
  OPPORTUNITY_PREVALLEY: "Possível vale (oportunidade)",
  RECENT_PREVALLEY: "Possível vale recente",
  REPLACEMENT_TOPK: "Substituição de Ativos em Carteira"
};

function labelStateUI(stateCodeOrLabel) {
  const s = String(stateCodeOrLabel || "").trim();
  if (!s) return "—";
  const up = s.toUpperCase();
  return VOCAB_STATE[up] || s;
}

function labelReasonUI(reasonCodeOrLabel) {
  const r = String(reasonCodeOrLabel || "").trim();
  if (!r) return "—";
  const up = r.toUpperCase();
  return VOCAB_REASON[up] || r;
}

/* =========================
   small utils
========================= */

function fmtMoney(x, currency = "BRL") {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  try {
    return n.toLocaleString("pt-BR", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });
  } catch {
    return `R$ ${n.toFixed(2)}`;
  }
}

function fmtQty(x, decimals = 2) {
  if (x == null) return "—";
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(decimals);
}

function todayISODateTimeLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function toNumSmart(v) {
  if (v == null) return NaN;
  const s0 = String(v).trim();
  if (!s0) return NaN;
  if (s0.includes(",")) return Number(s0.replace(/\./g, "").replace(",", "."));
  return Number(s0);
}

function normSymbol(sym) {
  if (!sym) return "";
  let s = String(sym).trim().toUpperCase().replace(/\s+/g, "");
  if (s.endsWith(".SA")) s = s.slice(0, -3);
  if (s.includes(".")) s = s.split(".")[0];
  return s;
}

function asArray(x) {
  return Array.isArray(x) ? x : [];
}

/* =========================
   ✅ adapters: payload -> UI
========================= */

function normalizeActions(actionsRaw) {
  const a = actionsRaw || {};
  const buy = asArray(a?.actions?.buy).map((x) => ({ ...x, symbol: normSymbol(x?.symbol) }));
  const sell = asArray(a?.actions?.sell).map((x) => ({ ...x, symbol: normSymbol(x?.symbol) }));

  const posMap = a?.wallet?.positions || {};
  const positions = {};
  for (const k of Object.keys(posMap)) {
    const sym = normSymbol(k);
    positions[sym] = { ...posMap[k], symbol: sym };
  }

  // mantém qualquer campo extra que o backend já manda (ex.: rank)
  return {
    ...a,
    asOf: a.asOf ?? null,
    account: a.account ?? null,
    capital: a.capital ?? null,
    wallet: { ...(a.wallet || {}), positions },
    actions: { buy, sell },
  };
}

/* =========================
   ✅ state helpers
========================= */

function stateLabelFromItem(it) {
  const st = it?.state != null ? String(it.state).trim() : "";
  if (st) return st;

  const d = it?.dyn || {};
  if (d.isTop) return "TOP";
  if (d.isPreTop) return "PRE_TOP";
  if (d.isValley) return "VALLEY";
  if (d.isPreValley) return "PRE_VALLEY";
  return "NEUTRAL";
}

function stateKindFromStateLabel(stateLabel) {
  const s = String(stateLabel || "").toUpperCase();
  if (s.includes("TOP")) return "sell";
  if (s.includes("VALLEY")) return "buy";
  if (s.includes("NEUTRAL")) return "watch";
  return "none";
}

/* =========================
   ✅ Chips
========================= */

function titleCaseWord(w) {
  const s = String(w || "").trim();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function sideRatingChipFromItem(it) {
  const sideRaw = String(it?.scoreParts?.side || "").trim().toLowerCase();
  const ratingRaw = it?.scoreParts?.rating ?? it?.dyn?.rating ?? null;

  const sideLabel = sideRaw ? titleCaseWord(sideRaw) : "";
  const ratingLabel = ratingRaw != null ? String(ratingRaw).trim() : "";

  if (!sideLabel && !ratingLabel) return null;

  const text = `${sideLabel} ${ratingLabel}`.trim();
  const kind =
    sideRaw === "bull" ? "buy" : sideRaw === "bear" ? "sell" : "watch";

  return { text, kind };
}

function fundChipFromItem(it) {
  const b = String(it?.fundamental_bucket || "").trim().toUpperCase();
  if (!b) return null;

  const ok = b === "A" || b === "B";
  const kind = ok ? "buy" : "sell";

  const titleParts = [];
  if (it?.fundamental_label) titleParts.push(String(it.fundamental_label));

  const fs = it?.fundamental_score;
  if (fs != null && Number.isFinite(Number(fs))) {
    titleParts.push(`score ${Number(fs)}`);
  }

  const title = titleParts.length ? titleParts.join(" • ") : "Fundamental";

  return { kind, text: b, title };
}

function AssetHeader({ symbol, name, sector, onClick }) {
  return (
    <div style={styles.assetHeader}>
      <button
        style={styles.assetSymbolBtn}
        onClick={onClick}
        title="Abrir detalhe do ativo"
        type="button"
      >
        {symbol}
      </button>

      {name ? (
        <div style={styles.assetNameBlock} title={name}>
          {name}
        </div>
      ) : null}

      {sector ? (
        <div style={styles.assetSectorBlock} title={sector}>
          {sector}
        </div>
      ) : null}
    </div>
  );
}

/* =========================
   ✅ defaults seguros
========================= */

const EMPTY_ACTIONS = Object.freeze({
  asOf: null,
  account: null,
  capital: null,
  wallet: { positions: {} },
  actions: { buy: [], sell: [] },
});

/* =========================
   component
========================= */

export default function BuyAndSell() {
  const { auth } = useAuth();
  const customerId = auth?.customerId || "anon";

  const SIDE_UI = { BUY: "COMPRA", SELL: "VENDA" };

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { settings, loading: settingsLoading } = useSettings();

  const ready = !!settings && !settingsLoading;
  const s = settings || {};

  const ROUTE_MONITORING = "/monitoringv2";
  const topK = s.planTopK ?? 5;
  const walletId = useWalletId();

  const [isNarrow, setIsNarrow] = useState(false);

  const [execOpen, setExecOpen] = useState(false);
  const [execItem, setExecItem] = useState(null);
  const [execSide, setExecSide] = useState("BUY");
  const [execQty, setExecQty] = useState("");
  const [execPrice, setExecPrice] = useState("");
  const [execWhen, setExecWhen] = useState(todayISODateTimeLocal());
  const [execSubmitting, setExecSubmitting] = useState(false);
  const [execErr, setExecErr] = useState(null);
  const [execMaxQty, setExecMaxQty] = useState(null);

  const [cashOpen, setCashOpen] = useState(false);
  const [cashValue, setCashValue] = useState("");
  const [cashSubmitting, setCashSubmitting] = useState(false);
  const [cashErr, setCashErr] = useState(null);

  useEffect(() => {
    const m = window.matchMedia("(max-width: 860px)");
    const onChange = () => setIsNarrow(Boolean(m.matches));
    onChange();
    try {
      m.addEventListener("change", onChange);
      return () => m.removeEventListener("change", onChange);
    } catch {
      m.addListener(onChange);
      return () => m.removeListener(onChange);
    }
  }, []);

  function openAsset(symbol) {
    const sym = normSymbol(symbol);
    if (!sym) return;
    navigate(`/assets?symbol=${encodeURIComponent(sym)}`);
  }

  function openExecuteModal({ side, item, lastPrice, maxQty }) {
    const suggestedQty = item?.suggestedQty != null ? Number(item.suggestedQty) : null;
    const suggestedValue = item?.suggestedValue != null ? Number(item.suggestedValue) : null;

    let px = null;
    if (lastPrice != null) px = Number(lastPrice);
    if ((px == null || !Number.isFinite(px)) && suggestedQty && suggestedValue) {
      px = suggestedValue / suggestedQty;
    }

    setExecErr(null);
    setExecSide(side);
    setExecItem(item);
    setExecMaxQty(Number.isFinite(Number(maxQty)) ? Number(maxQty) : null);

    setExecQty(
      suggestedQty != null && Number.isFinite(suggestedQty) ? String(suggestedQty) : ""
    );
    setExecPrice(px != null && Number.isFinite(px) ? String(px) : "");
    setExecWhen(todayISODateTimeLocal());
    setExecOpen(true);
  }

  function closeExecuteModal() {
    if (execSubmitting) return;
    setExecOpen(false);
    setExecItem(null);
    setExecErr(null);
    setExecMaxQty(null);
  }

  function openCashModal() {
    if (cashSubmitting) return;
    setCashErr(null);
    setCashValue("");
    setCashOpen(true);
  }

  function closeCashModal() {
    if (cashSubmitting) return;
    setCashOpen(false);
    setCashErr(null);
  }

  /* =========================
     ✅ React Query: ACTIONS + SUMMARY(QUOTES)
  ========================= */

  const actionsQS = useMemo(() => {
    if (!walletId) return "";
    const qs = new URLSearchParams({
      customerId: String(customerId), // ✅ extra (opcional)
      walletId: String(walletId),
      topK: String(topK),
    });
    return qs.toString();
  }, [walletId, topK]);

  const actionsQuery = useQuery({
    queryKey: ["buySell", customerId, "actions", actionsQS],
    enabled: ready && walletId != null,
    queryFn: async () => {
      const data = await apiFetch(`/api/portfolio/actions?${actionsQS}`);
      return normalizeActions(data);
    },
    staleTime: 60_000,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // ✅ quotes/nome para modal (price sugerido) + fallback de nome
  const summaryQuery = useQuery({
    queryKey: ["buySell", customerId, "walletSummary", walletId],
    enabled: ready && walletId != null,
    queryFn: async () => {
      const data = await apiFetch(
        `/api/wallet/summary?walletId=${encodeURIComponent(walletId)}`
      );
      return data || null;
    },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const actionsData = actionsQuery.data ?? EMPTY_ACTIONS;

  const summaryQuotesMap = useMemo(() => {
    const m = new Map();
    const arr = Array.isArray(summaryQuery.data?.quotes) ? summaryQuery.data.quotes : [];

    for (const q of arr) {
      const sym = normSymbol(q?.symbol || "");
      const px = Number(q?.lastPrice);

      const ch = Number(q?.change);
      const chPct01 = Number(q?.changePct01);

      const nameRaw =
        q?.name ??
        q?.shortName ??
        q?.longName ??
        q?.displayName ??
        q?.shortname ??
        q?.longname ??
        null;

      const name = typeof nameRaw === "string" ? nameRaw.trim() || null : null;

      if (sym) {
        m.set(sym, {
          lastPrice: Number.isFinite(px) ? px : null,
          change: Number.isFinite(ch) ? ch : null,
          changePct01: Number.isFinite(chPct01) ? chPct01 : null,
          name,
          asOf: q?.asOf ?? null,
          error: q?.error ?? null,
        });
      }
    }

    return m;
  }, [summaryQuery.data]);

  /* =========================
     ✅ Pull-to-refresh (só o necessário)
  ========================= */

  const refreshAll = useCallback(async () => {
    if (execOpen || cashOpen) return;

    await queryClient.invalidateQueries({ queryKey: ["buySell", customerId, "actions"] });
    await queryClient.invalidateQueries({ queryKey: ["buySell", customerId, "walletSummary", walletId] });

    await Promise.allSettled([actionsQuery.refetch(), summaryQuery.refetch()]);
  }, [queryClient, customerId, walletId, execOpen, cashOpen, actionsQuery, summaryQuery]);

  const ptr = usePullToRefresh({
    enabled: true,
    isBlocked: execOpen || cashOpen,
    startZoneY: 260,
    maxPull: 110,
    readyPull: 70,
    refreshHeight: 56,
    onRefresh: refreshAll,
  });

  /* =========================
     ✅ Guardrail (sem return cedo!)
  ========================= */

  const anyErrObj = actionsQuery.error || summaryQuery.error || null;

  const hasAnyData = !!actionsQuery.data || !!summaryQuery.data;
  const showFatal = ready && !hasAnyData && !!anyErrObj;

  const safeActionsData = showFatal ? EMPTY_ACTIONS : actionsData;
  const safeQuotesMap = showFatal ? new Map() : summaryQuotesMap;

  function assetMetaOf(symbol) {
    const sym = normSymbol(symbol);
    if (!sym) return { name: null, sector: null };

    const p = safeActionsData?.wallet?.positions?.[sym];
    if (p?.name || p?.sector) {
      return {
        name: typeof p.name === "string" ? p.name.trim() || null : null,
        sector: typeof p.sector === "string" ? p.sector.trim() || null : null,
      };
    }

    const r = Array.isArray(safeActionsData?.rank)
      ? safeActionsData.rank.find((x) => normSymbol(x?.symbol) === sym)
      : null;

    if (r?.name || r?.sector) {
      return {
        name: typeof r.name === "string" ? r.name.trim() || null : null,
        sector: typeof r.sector === "string" ? r.sector.trim() || null : null,
      };
    }

    const q = safeQuotesMap?.get?.(sym);
    const qName = typeof q?.name === "string" ? q.name.trim() : "";
    return { name: qName || null, sector: null };
  }

  async function submitExecute() {
    if (!execItem?.symbol) return;

    const qty = toNumSmart(execQty);
    const price = toNumSmart(execPrice);

    if (!Number.isFinite(qty) || qty <= 0) return setExecErr("Qty inválida.");
    if (!Number.isFinite(price) || price <= 0) return setExecErr("Price inválido.");

    if (execSide === "SELL" && execMaxQty != null && qty > execMaxQty + 1e-10) {
      return setExecErr(`Qty maior que a posição (${fmtQty(execMaxQty)}).`);
    }

    const value = qty * price;

    setExecSubmitting(true);
    setExecErr(null);

    try {
      await apiFetch(`/api/wallet/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          walletId: Number(walletId),
          side: execSide,
          symbol: normSymbol(execItem.symbol),
          qty: Number(qty.toFixed(8)),
          price: Number(price.toFixed(8)),
          value: Number(value.toFixed(2)),
          executedAt: execWhen,
          source: "plan",
        }),
      });

      await queryClient.invalidateQueries({ queryKey: ["buySell", customerId, "actions"] });
      await queryClient.invalidateQueries({
        queryKey: ["buySell", customerId, "walletSummary", walletId],
      });

      await actionsQuery.refetch();
      await summaryQuery.refetch();

      setExecOpen(false);
      setExecItem(null);
      setExecMaxQty(null);
    } catch (e) {
      console.error(e);
      setExecErr(e?.message || "Falha ao executar.");
    } finally {
      setExecSubmitting(false);
    }
  }

  async function submitCashAdjust() {
    if (!walletId) {
      setCashErr("walletId não encontrado (config).");
      return;
    }

    const delta = toNumSmart(cashValue);
    if (!Number.isFinite(delta) || delta <= 0) {
      setCashErr("Informe um valor > 0.");
      return;
    }

    setCashSubmitting(true);
    setCashErr(null);

    try {
      await apiFetch(`/api/wallet/cash/adjust`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          walletId: Number(walletId),
          delta: Number(delta.toFixed(2)),
        }),
      });

      await queryClient.invalidateQueries({ queryKey: ["buySell", customerId, "actions"] });
      await queryClient.invalidateQueries({
        queryKey: ["buySell", customerId, "walletSummary", walletId],
      });

      await actionsQuery.refetch();
      await summaryQuery.refetch();

      setCashOpen(false);
      setCashValue("");
    } catch (e) {
      console.error(e);
      setCashErr(e?.message || "Falha ao ajustar caixa.");
    } finally {
      setCashSubmitting(false);
    }
  }

  const walletInfo = useMemo(() => safeActionsData?.account || null, [safeActionsData]);
  const baseCurrency = useMemo(() => walletInfo?.baseCurrency || "BRL", [walletInfo]);

  const buyList = useMemo(() => asArray(safeActionsData?.actions?.buy), [safeActionsData]);
  const sellList = useMemo(() => asArray(safeActionsData?.actions?.sell), [safeActionsData]);
  const buyN = buyList.length;
  const sellN = sellList.length;

  const nextExpiry = useMemo(() => {
    const all = [...buyList, ...sellList];
    const days = all
      .map((x) => x?.expiresInDays)
      .filter((d) => d != null && Number.isFinite(Number(d)))
      .map(Number);
    if (!days.length) return null;
    return Math.max(0, Math.round(Math.min(...days)));
  }, [buyList, sellList]);

  const walletSymbolsSet = useMemo(() => {
    const out = new Set();
    const posMap = safeActionsData?.wallet?.positions || {};
    for (const sym0 of Object.keys(posMap)) {
      const sym = normSymbol(sym0);
      if (!sym || sym === "CASH") continue;
      const qty = Number(posMap[sym]?.qty ?? posMap[sym0]?.qty ?? 0);
      if (qty > 0) out.add(sym);
    }
    return out;
  }, [safeActionsData]);

  const holdListFiltered = useMemo(() => {
    const held = Array.from(walletSymbolsSet.values());
    const buySyms = new Set(buyList.map((x) => normSymbol(x?.symbol)).filter(Boolean));
    const sellSyms = new Set(sellList.map((x) => normSymbol(x?.symbol)).filter(Boolean));
    return held.filter((sym) => sym && !buySyms.has(sym) && !sellSyms.has(sym));
  }, [walletSymbolsSet, buyList, sellList]);

  const guideText = useMemo(() => {
    const cashUsable = Number(safeActionsData?.account?.cashUsable ?? 0);
    const toAllocate = Number(safeActionsData?.capital?.toAllocate ?? 0);

    if (buyN === 0 && sellN === 0) return "Hoje: nada a fazer. Apenas acompanhar.";
    if (buyN > 0 && sellN > 0)
      return `Hoje: ${buyN} compra(s)/aumento(s) e ${sellN} venda(s)/redução(ões).`;
    if (buyN > 0 && toAllocate <= 0)
      return "Hoje: existem compras sugeridas, mas não há capital livre (após reserva).";
    if (buyN > 0 && cashUsable <= 0)
      return "Hoje: existem compras sugeridas, mas você está sem cash usável.";
    if (buyN > 0) return `Hoje: ${buyN} compra(s)/aumento(s) sugerida(s).`;
    return `Hoje: ${sellN} venda(s)/redução(ões) sugerida(s).`;
  }, [safeActionsData, buyN, sellN]);

  const actionsLoading = actionsQuery.isLoading;
  const actionsErr = actionsQuery.error?.message || null;

  const topKMinScore = useMemo(() => {
    // Opção A (recomendada): usar o próprio buyList (já é topK elegível no backend)
    const scores = buyList
      .map((x) => Number(x?.score))
      .filter((v) => Number.isFinite(v));

    if (!scores.length) return null;
    return Math.min(...scores);
  }, [buyList]);


  return (
    <div
      style={styles.page}
      onTouchStart={ptr.handlers.onTouchStart}
      onTouchMove={ptr.handlers.onTouchMove}
      onTouchEnd={ptr.handlers.onTouchEnd}
      onTouchCancel={ptr.handlers.onTouchEnd}
    >
      <PullToRefreshHeader height={ptr.height} refreshing={ptr.refreshing} ready={ptr.ready} />

      {!ready ? (
        <div style={styles.bootOverlay}>
          <div style={styles.bootCard}>
            <div style={styles.bootTitle}>Carregando…</div>
            <div style={styles.bootSubtitle}>Preparando suas configurações do FinBot</div>
          </div>
        </div>
      ) : null}

      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, letterSpacing: -0.3 }}>Ações de Hoje</h2>
          <Pill>{buyN + sellN} ação(ões) hoje</Pill>
        </div>
      </header>

      {showFatal ? (
        <section style={styles.card}>
          <div style={{ fontWeight: 950, fontSize: 16, letterSpacing: -0.2 }}>
            Não foi possível atualizar agora
          </div>

          <div style={{ marginTop: 8, opacity: 0.78, lineHeight: 1.35 }}>
            {anyErrObj?.message || "Tente novamente em instantes."}
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              style={styles.btnPrimary}
              type="button"
              onClick={() => {
                actionsQuery.refetch();
                summaryQuery.refetch();
              }}
            >
              Tentar novamente
            </button>

            <button style={styles.btnGhost} type="button" onClick={() => navigate(ROUTE_MONITORING)}>
              Ir para Monitoring
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.6 }}>
            Dica: verifique sua conexão ou tente novamente em instantes.
          </div>
        </section>
      ) : null}

      {!showFatal ? (
        <>
          {actionsErr ? <div style={styles.error}>⚠ {actionsErr}</div> : null}
          {actionsLoading ? <div style={{ marginTop: 14, opacity: 0.85 }}>Carregando…</div> : null}

          {!actionsLoading && !actionsErr ? (
            <>
              {/* Contexto de hoje */}
              <section style={styles.card}>
                <div style={styles.cardTitleRow}>
                  <div style={styles.cardTitleInline}>
                    <FiInfo /> Contexto de hoje
                  </div>

                  <button
                    style={styles.smallBtn}
                    onClick={() => navigate(ROUTE_MONITORING)}
                    type="button"
                  >
                    <FiActivity style={{ marginRight: 6 }} /> Monitoring
                  </button>
                </div>

                <div style={styles.guideBox}>
                  <div style={{ fontWeight: 900 }}>{guideText}</div>
                  <div style={{ opacity: 0.7, fontSize: 12, marginTop: 4 }}>
                    as-of (close): <b>{safeActionsData?.asOf || "—"}</b>
                    {nextExpiry != null ? (
                      <span>
                        {" "}
                        • próxima expiração em <b>{nextExpiry}d</b>
                      </span>
                    ) : null}
                  </div>
                </div>

                <div style={styles.contextRow}>
                  <div style={styles.contextPills}>
                    <Pill>
                      Comprar: <b>{buyN}</b>
                    </Pill>
                    <Pill>
                      Vender: <b>{sellN}</b>
                    </Pill>

                    <Pill>
                      Disponível para investir:{" "}
                      <b>{fmtMoney(safeActionsData?.capital?.toAllocate, baseCurrency)}</b>
                    </Pill>
                    <Pill>
                      Reserva: <b>{fmtMoney(safeActionsData?.capital?.reserve, baseCurrency)}</b>
                    </Pill>
                    <Pill>
                      Caixa de vendas:{" "}
                      <b>{fmtMoney(safeActionsData?.capital?.cashFromSells, baseCurrency)}</b>
                    </Pill>
                  </div>
                </div>
              </section>

              {/* O que fazer agora */}
              <section style={styles.card}>
                <div style={styles.cardTitle}>O que fazer agora</div>

                {holdListFiltered.length ? (
                  <div style={{ marginBottom: 10 }}>
                    <HoldList
                      title="Manter / acompanhar"
                      kind="none"
                      symbols={holdListFiltered}
                      positionsMap={safeActionsData?.wallet?.positions || {}}
                      topKMinScore={topKMinScore}
                      onSymbolClick={openAsset}
                    />
                  </div>
                ) : null}

                <div style={styles.actionsSummaryOne}>
                  <div style={styles.actionsSummaryBox}>
                    <div style={styles.actionsK}>Cash usável</div>
                    <div style={styles.actionsV}>
                      <b>{fmtMoney(walletInfo?.cashUsable, baseCurrency)}</b>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.actionsGrid,
                    gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
                  }}
                >
                  <ActionListV3
                    title="COMPRAR / AUMENTAR"
                    kind="buy"
                    items={buyList}
                    emptyText="Nada para comprar/aumentar agora."
                    currency={baseCurrency}
                    getMeta={assetMetaOf}
                    onSymbolClick={openAsset}
                    onExecute={(item) =>
                      openExecuteModal({
                        side: "BUY",
                        item,
                        lastPrice: safeQuotesMap?.get?.(normSymbol(item?.symbol))?.lastPrice,
                        maxQty: null,
                      })
                    }
                    disableExecute={execSubmitting}
                  />

                  <ActionListV3
                    title="VENDER / REDUZIR"
                    kind="sell"
                    items={sellList}
                    emptyText="Sem venda/redução sugerida agora."
                    currency={baseCurrency}
                    getMeta={assetMetaOf}
                    onSymbolClick={openAsset}
                    onExecute={(item) =>
                      openExecuteModal({
                        side: "SELL",
                        item,
                        lastPrice: safeQuotesMap?.get?.(normSymbol(item?.symbol))?.lastPrice,
                        maxQty:
                          safeActionsData?.wallet?.positions?.[normSymbol(item?.symbol)]?.qty ??
                          null,
                      })
                    }
                    disableExecute={execSubmitting}
                  />
                </div>
              </section>
            </>
          ) : null}
        </>
      ) : null}

      {/* MODAL EXECUTAR */}
      {execOpen ? (
        <div style={styles.modalOverlay} onMouseDown={closeExecuteModal}>
          <div style={styles.modalCard} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ fontWeight: 900 }}>
                Executar {SIDE_UI[execSide] || execSide} •{" "}
                <span style={{ opacity: 0.9 }}>{execItem?.symbol || "—"}</span>
              </div>
              <button style={styles.iconBtn} onClick={closeExecuteModal} title="Fechar">
                <FiX />
              </button>
            </div>

            <div style={{ marginTop: 8, opacity: 0.75, fontSize: 12, lineHeight: 1.35 }}>
              Você está registrando uma operação <b>real</b> (não simulação). Isso atualiza sua carteira.
            </div>

            {execItem?.reason ? (
              <div style={{ marginTop: 8, opacity: 0.75, fontSize: 12 }}>
                Motivo: <b>{labelReasonUI(execItem.reason)}</b>
                {execItem?.triggerDate ? (
                  <span style={{ opacity: 0.8 }}> • trigger {execItem.triggerDate}</span>
                ) : null}
                {execItem?.expiresInDays != null ? (
                  <span style={{ opacity: 0.8 }}>
                    {" "}
                    • TTL {Math.max(0, Math.round(execItem.expiresInDays))}d
                  </span>
                ) : null}
              </div>
            ) : null}

            <div style={styles.modalGrid}>
              <label style={{ ...styles.modalField, gridColumn: "1 / 2" }}>
                <span style={styles.modalLabel}>Quando</span>
                <input
                  type="datetime-local"
                  value={execWhen}
                  onChange={(e) => setExecWhen(e.target.value)}
                  style={styles.modalInput}
                  disabled={execSubmitting}
                />
              </label>

              <label style={styles.modalField}>
                <div style={styles.modalLabelRow}>
                  <span style={styles.modalLabel}>Qty</span>

                  {execSide === "SELL" && execMaxQty != null ? (
                    <button
                      type="button"
                      style={styles.sellAllBtn}
                      onClick={() => setExecQty(String(execMaxQty))}
                      disabled={execSubmitting}
                      title="Preenche a Qty com 100% da posição atual na carteira"
                    >
                      Vender tudo ({fmtQty(execMaxQty)})
                    </button>
                  ) : null}
                </div>

                <input
                  value={execQty}
                  onChange={(e) => setExecQty(e.target.value)}
                  placeholder="ex: 1,23"
                  style={styles.modalInput}
                  disabled={execSubmitting}
                  inputMode="decimal"
                />
              </label>

              <label style={styles.modalField}>
                <span style={styles.modalLabel}>Price</span>
                <input
                  value={execPrice}
                  onChange={(e) => setExecPrice(e.target.value)}
                  placeholder="ex: 102,35"
                  style={styles.modalInput}
                  disabled={execSubmitting}
                  inputMode="decimal"
                />
              </label>

              <div style={{ ...styles.modalField, gridColumn: "1 / -1" }}>
                <span style={styles.modalLabel}>Valor (qty × price)</span>
                <div style={styles.modalValueBox}>
                  <b>
                    {(() => {
                      const q = toNumSmart(execQty);
                      const p = toNumSmart(execPrice);
                      if (!Number.isFinite(q) || !Number.isFinite(p)) return "—";
                      return fmtMoney(q * p, baseCurrency);
                    })()}
                  </b>
                </div>
              </div>
            </div>

            {execErr ? <div style={styles.error}>⚠ {execErr}</div> : null}

            <div style={styles.modalFooter}>
              <button style={styles.btnGhost} onClick={closeExecuteModal} disabled={execSubmitting}>
                Cancelar
              </button>
              <button style={styles.btnPrimary} onClick={submitExecute} disabled={execSubmitting}>
                {execSubmitting ? "Executando…" : "Confirmar execução"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ✅ MODAL CAPITAL INICIAL */}
      {cashOpen ? (
        <div style={styles.modalOverlay} onMouseDown={closeCashModal}>
          <div style={styles.modalCardSmall} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ fontWeight: 900 }}>Definir capital inicial</div>
              <button style={styles.iconBtn} onClick={closeCashModal} title="Fechar">
                <FiX />
              </button>
            </div>

            <div style={{ marginTop: 8, opacity: 0.75, fontSize: 12, lineHeight: 1.35 }}>
              Isso adiciona caixa na carteira (MVP). Você pode ajustar depois.
            </div>

            <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
              <label style={styles.modalField}>
                <span style={styles.modalLabel}>Valor (R$)</span>
                <input
                  value={cashValue}
                  onChange={(e) => setCashValue(e.target.value)}
                  placeholder="ex: 10.000,00"
                  style={styles.modalInput}
                  disabled={cashSubmitting}
                  inputMode="decimal"
                />
              </label>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[1000, 5000, 10000, 20000].map((v) => (
                  <button
                    key={v}
                    type="button"
                    style={styles.quickChip}
                    onClick={() => setCashValue(String(v))}
                    disabled={cashSubmitting}
                    title="Preencher valor"
                  >
                    {fmtMoney(v, baseCurrency)}
                  </button>
                ))}
              </div>

              <div style={{ ...styles.modalField }}>
                <span style={styles.modalLabel}>Preview</span>
                <div style={styles.modalValueBox}>
                  <b>
                    {(() => {
                      const n = toNumSmart(cashValue);
                      if (!Number.isFinite(n) || n <= 0) return "—";
                      return fmtMoney(n, baseCurrency);
                    })()}
                  </b>
                </div>
              </div>
            </div>

            {cashErr ? <div style={styles.error}>⚠ {cashErr}</div> : null}

            <div style={styles.modalFooter}>
              <button style={styles.btnGhost} onClick={closeCashModal} disabled={cashSubmitting}>
                Cancelar
              </button>
              <button style={styles.btnPrimary} onClick={submitCashAdjust} disabled={cashSubmitting}>
                {cashSubmitting ? "Salvando…" : "Adicionar ao caixa"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* =========================
   subcomponents
========================= */

function PillTiny({ kind = "none", children, title }) {
  const s = {
    ...styles.pillTiny,
    ...(kind === "buy" ? styles.pillTinyBuy : null),
    ...(kind === "sell" ? styles.pillTinySell : null),
    ...(kind === "watch" ? styles.pillTinyWatch : null),
    ...(kind === "setup" ? styles.pillTinySetup : null),
    ...(kind === "imminent" ? styles.pillTinyImminent : null),
    ...(kind === "none" ? styles.pillTinyNone : null),
  };
  return (
    <span style={s} title={title}>
      {children}
    </span>
  );
}

function CriteriaLineV2({ it }) {
  const stateRaw = stateLabelFromItem(it);
  const stateKind = stateKindFromStateLabel(stateRaw);
  const stateLabel = labelStateUI(stateRaw);

  const reasonHuman = labelReasonUI(it?.reason);

  return (
    <>
      <div style={styles.criteriaLine}>
        <PillTiny kind={stateKind} title="estado">
          {stateLabel}
        </PillTiny>

        {it?.score != null ? (
          <PillTiny kind="none" title="score">
            score: {Number(it.score).toFixed(1)}
          </PillTiny>
        ) : null}
      </div>

      <div style={styles.whyLine}>
        <span style={styles.whyK}>Por quê:</span>
        <span style={styles.whyV}>
          <b>{reasonHuman}</b>
        </span>
      </div>
    </>
  );
}

function ActionListV3({
  title,
  kind,
  items,
  emptyText,
  currency,
  onExecute,
  disableExecute,
  onSymbolClick,
  getMeta,
}) {
  const list = asArray(items);

  return (
    <div style={styles.actionBlock}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontWeight: 900 }}>{title}</div>
        <Badge kind={kind}>{list.length}</Badge>
      </div>

      <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
        {list.length ? (
          list.map((it) => {
            const sym = normSymbol(it.symbol);
            const meta = getMeta?.(sym) || {};

            const sideChip = sideRatingChipFromItem(it);
            const fundChip = fundChipFromItem(it);

            return (
              <div key={`${kind}-${sym}`} style={styles.actionRowV2}>
                <div style={styles.actionLeft}>
                  <AssetHeader
                    symbol={sym}
                    name={meta.name}
                    sector={meta.sector}
                    onClick={() => onSymbolClick?.(sym)}
                  />

                  <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {sideChip ? <PillTiny kind={sideChip.kind}>{sideChip.text}</PillTiny> : null}
                    {fundChip ? (
                      <PillTiny kind={fundChip.kind} title={fundChip.title}>
                        {fundChip.text}
                      </PillTiny>
                    ) : null}
                  </div>

                  <CriteriaLineV2 it={it} />
                </div>

                <div style={styles.actionRight}>
                  <div style={{ fontWeight: 900 }}>{fmtMoney(it.suggestedValue, currency)}</div>

                  <div style={{ fontSize: 12, opacity: 0.75 }}>qty: {fmtQty(it.suggestedQty)}</div>

                  {onExecute ? (
                    <button
                      style={styles.executeBtn}
                      onClick={() => onExecute(it)}
                      disabled={disableExecute}
                      type="button"
                    >
                      Executar
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ opacity: 0.65 }}>{emptyText}</div>
        )}
      </div>
    </div>
  );
}

function HoldList({ title, kind, symbols, onSymbolClick, fullWidth, positionsMap, topKMinScore }) {
  const list = asArray(symbols);

  function chipModelFor(sym) {
    const p = positionsMap?.[sym] || null;

    const score = p?.score;
    const scoreNum = Number(score);
    const hasScore = Number.isFinite(scoreNum);

    const snapshotMissing = p?.snapshotMissing === true;

    const belowCutoff =
      hasScore && Number.isFinite(Number(topKMinScore)) ? scoreNum < Number(topKMinScore) : false;

    // texto: "ALLD3  54.2" (sutil e compacto)
    const text = hasScore ? `${sym}  ${scoreNum.toFixed(1)}` : sym;

    // decide estilo do chip
    let chipStyle = styles.chipBtn;
    let title = "Abrir detalhe do ativo";

    if (snapshotMissing) {
      chipStyle = { ...styles.chipBtn, ...styles.chipBtnNoSnap };
      title = "Sem snapshot recente (score indisponível)";
    } else if (belowCutoff) {
      chipStyle = { ...styles.chipBtn, ...styles.chipBtnBelowCutoff };
      title = `Score abaixo do corte do TopK (${Number(topKMinScore).toFixed(1)})`;
    } else if (hasScore) {
      chipStyle = { ...styles.chipBtn, ...styles.chipBtnHasScore };
      title = `Score ${scoreNum.toFixed(1)}`;
    }

    return { text, chipStyle, title };
  }

  return (
    <div style={{ ...styles.actionBlock, ...(fullWidth ? styles.fullWidth : null) }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
        <div style={{ fontWeight: 900, opacity: 0.95 }}>{title}</div>
        <Badge kind={kind}>{list.length}</Badge>
      </div>

      {/* opcional: mostrar o corte, bem discreto */}
      {Number.isFinite(Number(topKMinScore)) ? (
        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.6 }}>
          Corte TopK (menor score em Comprar/Aumentar): <b>{Number(topKMinScore).toFixed(1)}</b>
        </div>
      ) : null}

      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
        {list.length ? (
          list.map((sym) => {
            const m = chipModelFor(sym);
            return (
              <button
                key={sym}
                style={m.chipStyle}
                onClick={() => onSymbolClick?.(sym)}
                title={m.title}
                type="button"
              >
                {m.text}
              </button>
            );
          })
        ) : (
          <span style={{ opacity: 0.6 }}>—</span>
        )}
      </div>
    </div>
  );
}


function Badge({ kind, children }) {
  const s = {
    ...styles.badge,
    ...(kind === "buy" ? styles.badgeBuy : null),
    ...(kind === "sell" ? styles.badgeSell : null),
    ...(kind === "watch" ? styles.badgeWatch : null),
    ...(kind === "setup" ? styles.badgeSetup : null),
    ...(kind === "imminent" ? styles.badgeImminent : null),
    ...(kind === "risk" ? styles.badgeRisk : null),
    ...(kind === "none" ? styles.badgeNone : null),
  };
  return <span style={s}>{children}</span>;
}

function Pill({ children }) {
  return <span style={styles.pill}>{children}</span>;
}

/* =========================
   styles (inalterado)
========================= */

const styles = {
  page: { padding: 14, maxWidth: 1100, margin: "0 auto" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginBottom: 12,
  },

  bootOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 14,
    zIndex: 1200,
  },
  bootCard: {
    width: "min(520px, 100%)",
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(20,20,24,0.98)",
    padding: 16,
    boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
  },
  bootTitle: { fontWeight: 950, fontSize: 16, opacity: 0.95 },
  bootSubtitle: { marginTop: 6, fontSize: 12, opacity: 0.7, lineHeight: 1.35 },

  error: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    background: "rgba(255,120,120,0.10)",
    border: "1px solid rgba(255,120,120,0.25)",
    color: "rgba(255,255,255,0.92)",
  },

  card: {
    marginTop: 12,
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
  },
  cardTitle: {
    fontWeight: 900,
    opacity: 0.95,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
  },
  cardTitleInline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 900,
    opacity: 0.95,
  },

  guideBox: {
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.03)",
    borderRadius: 14,
    padding: 12,
    marginBottom: 12,
  },

  contextRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  contextPills: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },

  smallBtn: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.95)",
    cursor: "pointer",
    fontWeight: 800,
    gap: 6,
    whiteSpace: "nowrap",
  },

  actionsSummaryOne: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "1fr",
    marginBottom: 10,
  },
  actionsSummaryBox: {
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.03)",
    borderRadius: 14,
    padding: 12,
  },
  actionsK: { fontSize: 12, opacity: 0.65 },
  actionsV: { fontSize: 18, opacity: 0.95, marginTop: 2 },

  actionsGrid: { display: "grid", gap: 10 },

  actionBlock: {
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.03)",
    borderRadius: 14,
    padding: 12,
  },
  fullWidth: { gridColumn: "1 / -1" },

  actionRowV2: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.04)",
  },
  actionLeft: { minWidth: 0 },
  actionRight: {
    textAlign: "right",
    minWidth: 120,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: "flex-end",
  },

  executeBtn: {
    padding: "8px 10px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.95)",
    cursor: "pointer",
  },

  chipBtn: {
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.92)",
    borderRadius: 999,
    padding: "8px 10px",
    cursor: "pointer",
  },

  criteriaLine: { marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 },
  whyLine: {
    marginTop: 6,
    fontSize: 12,
    opacity: 0.85,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  whyK: { opacity: 0.7 },
  whyV: { opacity: 0.95 },

  badge: {
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    opacity: 0.95,
  },
  badgeBuy: { background: "rgba(120,255,170,0.10)", border: "1px solid rgba(120,255,170,0.22)" },
  badgeSell: { background: "rgba(255,120,120,0.10)", border: "1px solid rgba(255,120,120,0.22)" },
  badgeWatch: { background: "rgba(140,200,255,0.10)", border: "1px solid rgba(140,200,255,0.22)" },
  badgeSetup: { background: "rgba(255,210,120,0.10)", border: "1px solid rgba(255,210,120,0.22)" },
  badgeImminent: { background: "rgba(255,170,220,0.10)", border: "1px solid rgba(255,170,220,0.22)" },
  badgeRisk: { background: "rgba(255,170,220,0.10)", border: "1px solid rgba(255,170,220,0.22)" },
  badgeNone: {},

  pill: {
    fontSize: 12,
    padding: "5px 10px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.05)",
    opacity: 0.95,
  },

  pillTiny: {
    fontSize: 11,
    padding: "3px 8px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    opacity: 0.95,
  },
  pillTinyBuy: { background: "rgba(120,255,170,0.10)", border: "1px solid rgba(120,255,170,0.22)" },
  pillTinySell: { background: "rgba(255,120,120,0.10)", border: "1px solid rgba(255,120,120,0.22)" },
  pillTinyWatch: { background: "rgba(140,200,255,0.10)", border: "1px solid rgba(140,200,255,0.22)" },
  pillTinySetup: { background: "rgba(255,210,120,0.10)", border: "1px solid rgba(255,210,120,0.22)" },
  pillTinyImminent: { background: "rgba(255,170,220,0.10)", border: "1px solid rgba(255,170,220,0.22)" },
  pillTinyNone: {},

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 14,
    zIndex: 1000,
  },
  modalCard: {
    width: "min(860px, 100%)",
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(20,20,24,0.98)",
    padding: 14,
    boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
  },
  modalCardSmall: {
    width: "min(560px, 100%)",
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(20,20,24,0.98)",
    padding: 14,
    boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
  },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 },

  iconBtn: {
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.95)",
    borderRadius: 12,
    padding: "8px 10px",
    cursor: "pointer",
  },

  modalGrid: {
    marginTop: 12,
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr 1fr",
    gap: 10,
  },

  modalField: { display: "grid", gap: 6 },
  modalLabel: { fontSize: 12, opacity: 0.7 },

  modalLabelRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },

  sellAllBtn: {
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.92)",
    borderRadius: 999,
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  modalInput: {
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.95)",
    outline: "none",
    fontSize: 16,
  },

  modalValueBox: {
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.04)",
    color: "rgba(255,255,255,0.95)",
    fontSize: 16,
  },

  modalFooter: { marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 10 },

  btnGhost: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "transparent",
    color: "rgba(255,255,255,0.92)",
    cursor: "pointer",
  },
  btnPrimary: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.10)",
    color: "rgba(255,255,255,0.96)",
    cursor: "pointer",
    fontWeight: 850,
  },

  quickChip: {
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.92)",
    borderRadius: 999,
    padding: "8px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  assetHeader: {
    display: "grid",
    gap: 2,
    marginBottom: 4,
  },

  assetSymbolBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    color: "rgba(255,255,255,0.95)",
    fontWeight: 950,
    fontSize: 15,
    cursor: "pointer",
    textAlign: "left",
  },

  assetNameBlock: {
    fontSize: 12,
    opacity: 0.78,
    maxWidth: 420,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  assetSectorBlock: {
    fontSize: 11,
    opacity: 0.55,
    maxWidth: 420,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chipBtnHasScore: {
  // só um “reforço” de leitura (quase imperceptível)
  border: "1px solid rgba(255,255,255,0.14)",
  background: "rgba(255,255,255,0.07)",
  },

  chipBtnBelowCutoff: {
    // “atenção sutil”: levemente mais quente/rosado, sem gritar
    border: "1px solid rgba(255,160,160,0.22)",
    background: "rgba(255,160,160,0.08)",
  },

  chipBtnNoSnap: {
    // cinza (sem snapshot)
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.03)",
    opacity: 0.75,
  },
};
